import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { ReadMoreComponent } from '../../app/content/layout/components/general/read-more/read-more.component';

const meta: Meta<ReadMoreComponent> = {
  title: 'Atoms/ReadMore',
  component: ReadMoreComponent,
  parameters: {
    docs: {
      description: {
        component: 'A component that allows expanding and collapsing text content with a "read more/less" toggle functionality.',
      },
    },
  },
  argTypes: {
    useReadMore: {
      control: { type: 'boolean' },
      description: 'Whether to enable read more functionality',
    },
    showFadeOut: {
      control: { type: 'boolean' },
      description: 'Whether to show fade out effect',
    },
    backgroundColor: {
      control: { type: 'color' },
      description: 'Background color for fade effect',
    },
    useBootstrapClasses: {
      control: { type: 'boolean' },
      description: 'Whether to use Bootstrap classes for styling',
    },
    readMoreText: {
      control: { type: 'text' },
      description: 'Text for the "read more" button',
    },
    readLessText: {
      control: { type: 'text' },
      description: 'Text for the "read less" button',
    },
    maxBodyHeight: {
      control: { type: 'number' },
      description: 'Maximum height before showing read more',
    },
    eventSourceName: {
      control: { type: 'text' },
      description: 'Event source name for tracking',
    },
  },
};

export default meta;
type Story = StoryObj<ReadMoreComponent>;

const longContent = `
  <div>
    <p>Dette er en lang tekst der demonstrerer read more funktionaliteten. 
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor 
    incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis 
    nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
    
    <p>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore 
    eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt 
    in culpa qui officia deserunt mollit anim id est laborum.</p>
    
    <p>Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium 
    doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore 
    veritatis et quasi architecto beatae vitae dicta sunt explicabo.</p>
  </div>
`;

export const Default: Story = {
  args: {
    useReadMore: true,
    showFadeOut: true,
    backgroundColor: '#ffffff',
    useBootstrapClasses: false,
    readMoreText: 'Læs mere',
    readLessText: 'Læs mindre',
    maxBodyHeight: 100,
  },
  render: (args) => ({
    props: args,
    template: `
      <div app-read-more 
           [useReadMore]="useReadMore"
           [showFadeOut]="showFadeOut"
           [backgroundColor]="backgroundColor"
           [useBootstrapClasses]="useBootstrapClasses"
           [readMoreText]="readMoreText"
           [readLessText]="readLessText"
           [maxBodyHeight]="maxBodyHeight"
           [eventSourceName]="eventSourceName">
        ${longContent}
      </div>
    `,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('[app-read-more]')).toBeTruthy();
  },
};

export const WithBootstrap: Story = {
  args: {
    ...Default.args,
    useBootstrapClasses: true,
  },
  render: Default.render,
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('[app-read-more]')).toBeTruthy();
  },
};

export const NoFadeOut: Story = {
  args: {
    ...Default.args,
    showFadeOut: false,
  },
  render: Default.render,
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('[app-read-more]')).toBeTruthy();
  },
};

export const CustomHeight: Story = {
  args: {
    ...Default.args,
    maxBodyHeight: 200,
  },
  render: Default.render,
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('[app-read-more]')).toBeTruthy();
  },
};
